// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    extraContent?: InjectableCss;
}

export const productGridContainerStyle: ProductGridContainerStyle = {};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});

const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Info = "Info",
    AddToCartActions = "AddToCartActions",
    Price = "Price",
    Availability = "Availability",
    AddToList = "AddToList",
    ExtraContent = "ExtraContent",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructedChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AddToList),
    ];
};

export interface ProductGridContainerProps extends PropsWithChildren {
    extendedStyles?: ProductGridContainerStyle;
}

const ProductGridContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: ProductGridContainerProps & React.HTMLAttributes<HTMLElement>) => {
    const [styles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, info, addToCartActions, price, availability, addToList, extraContent] = React.useMemo(
        () => getDestructedChildren(childrenArray),
        [childrenArray],
    );
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper as="article" {...styles.container} {...otherProps} className={sharedClassName}>
                <StyledWrapper as="figure" className="top-section">
                    {image}
                </StyledWrapper>
                <StyledWrapper as="section" className="middle-section">
                    {info}
                </StyledWrapper>
                <StyledWrapper as="footer" className="bottom-section">
                    <StyledWrapper className="price-section">
                        {price}
                        {availability}
                    </StyledWrapper>
                    {addToCartActions}
                    {addToList}
                </StyledWrapper>
                {extraContent}
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} className={`${sharedClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};

const Price: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};
const ExtraContent: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { extraContent },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...extraContent} className={`${sharedClassName}-extra`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;
ProductGridContainer.Info = Info;
ProductGridContainer.Info.displayName = NAMES.Info;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;
ProductGridContainer.ExtraContent = ExtraContent;
ProductGridContainer.ExtraContent.displayName = NAMES.ExtraContent;

export default ProductGridContainer;
